import { contexts, hooks } from 'lib_ui-primitives';
import { constants, metadata } from 'lib_ui-services';
import lodash from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import useActiveRecord from './useActiveRecord';
import useDefaultValueForHNode from './useDefaultValueForHNode';
import useEventSink from './useEventSink';
import useFormContext from './useFormContext';
const { useMemoizedObject } = hooks;

// For comparing hook runs to see what changed.  See 'FOR DEBUGGING' comment below
// import { useWhatChanged } from '@simbathesailor/use-what-changed';
import { useMoveFocusToNext } from '../components/contextProviders/FocusProvider/useMoveFocusToNext';
import useGetMemoized from './useGetMemoized';

const { isEqual } = lodash;
const { get } = lodash;
// Avoid rerenders by using a constant for this.
const DEFAULT_LOCAL_ERROR_MESSAGES = [];
const { getPathToProperty } = metadata;
/**
 *
 * @param {Object} props
 * @param {Object} props.hNode
 * @param {string} [props.hNode.title]
 * @param {string} [props.hNode.autocomplete='off']
 * @param {string} props.hNode.defaultValue
 * @param {string} props.hNode.foreignNamespace
 * @param {string} props.hNode.foreignRelation
 * @param {string} props.hNode.propertyName
 * @param {string} props.hNode.propertyPath
 * @param {boolean} props.hNode.readOnly
 * @param {Array} [props.hNode.readOnlyFor]
 * @param {(event: import('react').SyntheticEvent)=>void} [onFocus]
 * @param {(event: import('react').SyntheticEvent)=>void} [onBlur]
 */
export default function useFormControl(props, overrideDefaultValue) {
    const { onFocus: _onFocus, onBlur: _onBlur, onChange } = props || {};
    const session = useContext(contexts.SessionContext);
    const [active, setActive] = useState(false);
    const { namespace, relation, type, formInstanceId } = useActiveRecord();
    const { disabled, newRecord, oldRecord, errorsByPath = {}, autoFocusHNodeId } = useFormContext();
    const [subscribe, publish] = useEventSink();
    const {
        hNode,
        hNode: { title, propertyName, foreignNamespace, foreignRelation, autocomplete = 'off' }
    } = props;
    const path = getPathToProperty(hNode);
    const [localErrorMessages, setLocalErrorMessages] = useState(DEFAULT_LOCAL_ERROR_MESSAGES);
    const { defaultValue, defaultReady } = useDefaultValueForHNode(hNode, overrideDefaultValue);
    const oldValue = useGetMemoized(oldRecord, path, defaultValue);
    const newValue = useGetMemoized(newRecord, path);
    const isDefaultValue = newValue == null || newValue?.isDefaultRecord;
    const value = newValue == null ? defaultValue : newValue;
    const moveFocusToNext = useMoveFocusToNext(hNode.id);
    const shouldPersistPreviousShortTextValue =
        hNode.hNodeType === 'ShortText' && hNode.transforms?.some(t => t.hNodeType === 'DefaultToPreviousSelection');

    const doAfterBarcodeScan = useCallback(() => {
        if (hNode.transforms?.some(t => t.hNodeType === 'TabAfterScan')) {
            moveFocusToNext();
        } else {
            const postScanActions = hNode.transforms?.filter(t => t.hNodeType === 'ActionAfterScan') || [];
            postScanActions.forEach(action => {
                const { namespace, relation, forAction, subtype } = action;
                const unsubscribe = subscribe({ verb: 'change', namespace, relation, status: 'success' }, () => {
                    unsubscribe();
                    publish(hNode, { verb: forAction, namespace, relation, type: subtype });
                });
            });
        }
    }, [hNode, moveFocusToNext, publish, subscribe]);

    let errors = get(errorsByPath, path, []);
    if (!Array.isArray(errors)) errors = [errors];
    errors = useMemoizedObject([...errors, ...localErrorMessages]);
    const isDirty = !isEqual(oldValue, value);

    const sessionRoleTitle = session?.role?.title;
    // The array of roles in the readOnlyFor property of an hNode only contains well known roles,
    // so if the session role is not a well known role e.g. a custom role, then treat it as ADMIN
    // We follow the same rules as other backend rules for custom roles,
    // which is to default the role to the ADMIN role
    const accessRole = Object.keys(constants.WELL_KNOWN_ROLES).includes(sessionRoleTitle)
        ? sessionRoleTitle
        : 'ADMIN';
    const readOnly = (hNode.readOnlyFor ?? []).includes(accessRole) || hNode.readOnly;

    const setValue = useCallback(
        (changeValue, inputSource) => {
            const { id, foreignNamespace, foreignRelation, propertyName, propertyOverride, propertyPath = '' } = hNode;
            let newValue = changeValue;
            // This allows setValue to behave like the typical setValue from a useState method
            // (where sometimes a function is passed in instead of the actual value)
            if (typeof changeValue === 'function') {
                newValue = changeValue(value);
            }
            // Deep comparison for arrays - Typically used for WorkflowCriteria
            if (Array.isArray(newValue) && Array.isArray(value) && isEqual(newValue, value)) {
                return;
            }
            // Typically something like a dropdown would pass a selected item object into here that needs cleaning up
            if (typeof newValue === 'object' && !(newValue instanceof Date) && !Array.isArray(newValue)) {
                // If the control's default record was selected, then corresponding field
                // value should be removed from the record.
                if (newValue.isDefaultRecord) {
                    newValue = undefined;
                } else {
                    if (foreignNamespace && foreignRelation) {
                        // avoid rerenders by using the same ref if possible
                        if (isEqual(value, newValue)) {
                            newValue = value;
                        } else {
                            // Remove metadata, etc. from selected record
                            newValue = metadata.getMinimumForeignKeyFields(changeValue, hNode);
                        }
                    }
                }
                publish(
                    {
                        newValue,
                        inputSource
                    },
                    { verb: 'controlChange', hNodeId: id }
                );
            }
            // Clear out a saved default value if necessary (see useDefaultValueForHNode.js)
            if (newValue == null && value != null && !shouldPersistPreviousShortTextValue) {
                publish(
                    {
                        newValue,
                        inputSource
                    },
                    { verb: 'controlChange', hNodeId: id }
                );
            }
            if (shouldPersistPreviousShortTextValue && typeof newValue !== 'object' && !Array.isArray(newValue)) {
                publish(
                    {
                        newValue: newValue === '' ? undefined : newValue,
                        inputSource
                    },
                    { verb: 'controlChange', hNodeId: id }
                );
            }
            setLocalErrorMessages(DEFAULT_LOCAL_ERROR_MESSAGES);
            // Typically consumed by the form state machine (see useFormMachine.js)
            publish(
                {
                    propertyName,
                    // The foreign relation still controls lookup/query behavior, but propertyOverride
                    // controls where the selected foreign key is stored on the edited record.
                    propertyOverride,
                    foreignNamespace,
                    foreignRelation,
                    newValue,
                    inputSource,
                    propertyPath
                },
                { verb: 'beginChange', namespace, relation, type, formInstanceId }
            );
            onChange?.(newValue);
            if (inputSource === constants.sensorTypes.BARCODE) {
                doAfterBarcodeScan();
            }
        },
        [
            namespace,
            relation,
            type,
            formInstanceId,
            hNode,
            publish,
            value,
            onChange,
            doAfterBarcodeScan,
            shouldPersistPreviousShortTextValue
        ]
    );

    const onFocus = useCallback(
        /**
         * @param {import('react').SyntheticEvent} event
         */
        event => {
            setActive(true);
            event.target.select?.();
            _onFocus?.();
        },
        [_onFocus]
    );

    const onBlur = useCallback(
        e => {
            // Do not blur if the user clicked on a scan button (https://stackoverflow.com/a/60094794/1356444).
            // (relatedTarget will be the scan button in that case).
            if (e.relatedTarget == null || !e.currentTarget.contains(e.relatedTarget)) {
                setActive(false);
                _onBlur?.();
            }
        },
        [_onBlur]
    );
    // FOR DEBUGGING:
    // If you need to compare runs of the following useEffect hook to see what changed, uncomment the following two lines
    // and put `deps` as your dependency array.  Also verify that these lines still has the same dependencies as the hook. :)
    // const deps = [value, foreignNamespace, foreignRelation, publish];
    // useWhatChanged(deps, 'value, foreignNamespace, foreignRelation, publish');

    useEffect(() => {
        // Inform filters (e.g. foreignRelationSelectionOnForm) for other dropdowns etc
        // that need to react to a value change.
        // Typically consumed by FilterInterdependencyBoundary.js
        //
        // MUST be a passive useEffect (NOT useLayoutEffect). The boundary subscribes to
        // these select:success events in a useLayoutEffect; React runs ALL layout effects
        // before ALL passive effects, so a passive publish here guarantees the boundary is
        // already listening when a dropdown publishes its INITIAL value on mount (#5528,
        // 2022). If this is a layout effect, both the publish and the boundary's subscribe
        // are layout effects and run child-before-parent — the boundary subscribes AFTER the
        // children publish, misses their initial selections (they bubble to the outer
        // boundary), its store stays empty, and useIsDisabledByFilter reads "no parent
        // selected" -> Building/Room render disabled on load. That regression (c30f706800)
        // is the disable bug. Same-commit child clearing on a USER change no longer needs a
        // layout effect here: it is handled synchronously in DropDown onChange ->
        // clearChildDisplays (before setValue), so there is no native flash.
        if (foreignNamespace != null && foreignRelation != null) {
            publish(
                { value },
                {
                    verb: 'select',
                    namespace: foreignNamespace,
                    relation: foreignRelation,
                    status: 'success'
                }
            );
        }
    }, [value, foreignNamespace, foreignRelation, publish]);

    return {
        active,
        autoComplete: isDirty ? 'off' : autocomplete,
        disabled: disabled || readOnly,
        errors,
        isDirty,
        onBlur,
        onFocus,
        autoFocus: autoFocusHNodeId === hNode.id,
        setValue,
        title: title ?? propertyName,
        value,
        isDefaultValue,
        defaultReady
    };
}
